import { useEffect, useState } from "react";
import {
  createOfflineLicenseRequest,
  createOrgRequest,
  deleteOrgRequest,
  getOrgsOfDistributor,
  getOrgsRequests,
} from "@utils/api-communication/RestApiCli";
import {
  Button,
  Card,
  Container,
  Drawer,
  Flex,
  Title,
  Text,
  Divider,
  Group,
  Switch,
  Menu,
  Burger,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  OrganizationAccountRequestForm,
  showCreateOrgRequestError,
} from "../distributor_dashboard/OrganizationAccountRequestForm";
import OrganizationRequestList from "../distributor_dashboard/OrganizationRequestList";
import OrganizationManagementDashboard from "../organization_dashboard/OrganizationManagementDashboard";
import OfflineLicenseRequestForm from "../distributor_dashboard/OfflineLicenseRequestForm";
import OfflineLicenseRequestList from "../distributor_dashboard/OfflineLicenseRequestList";
import { useTranslation } from "react-i18next";
import SubmissionsList from "@components/submissions/SubmissionsList";
import PaginatedOrganizations from "../common/PaginatedOrganizations";
import { OrganizationListRow } from "../common/organizationListQuery";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserDistributorId } from "@utils/redux-utils/selectors/userSelectors";
import { DISTRIBUTOR_DOCS_FOLDER_URL } from "@constants/externalTutorialLinks";

export default function ManagerDashboard(_props) {
  const [organizations, setOrganizations] = useState([]);
  const [distId, setDistId] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [orgRequests, setOrgRequests] = useState([]);
  const [openedOrgRequest, { open: openOrgRequest, close: closeOrgRequest }] = useDisclosure(false);
  const [openedOfflineRequest, { open: openOfflineRequest, close: closeOfflineRequest }] =
    useDisclosure(false);
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationListRow | null>(
    null,
  );
  const [menuOpened, setMenuOpened] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userDistId = useSelector(selectUserDistributorId);

  useEffect(() => {
    if (!userDistId) return;
    // Always enforce the manager's own distId — redirect if URL param is missing or wrong
    const urlSearchParams = new URLSearchParams(location.search);
    const id = urlSearchParams.get("id");
    if (id !== userDistId) {
      navigate(`/manager-dashboard?id=${userDistId}`, { replace: true });
      return;
    }
    setDistId(userDistId);
  }, [location.search, openedOrgRequest, userDistId]);

  useEffect(() => {
    if (distId != null) {
      updateLists();
    }
  }, [distId]);

  useEffect(() => {
    setSelectedOrganization((current) => {
      if (!current) return current;
      const fresh = organizations.find((org) => org.orgId === current.orgId);
      return fresh ? { ...current, ...fresh } : current;
    });
  }, [organizations]);

  const updateLists = () => {
    getOrgsOfDistributor(
      distId ? { distId: distId } : {},
      (res) => {
        setOrganizations(res.data);
      },
      (error) => {
        console.error(error);
      },
    );

    getOrgsRequests(
      distId ? { id: distId } : {},
      (res) => {
        setOrgRequests(res.data);
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const handleDeleteRequest = (id) => {
    deleteOrgRequest(
      { id },
      (res) => {
        updateLists();
        console.log(res);
      },
      (error) => {
        console.error(error);
      },
    );
  };

  return (
    <Container fluid>
      {selectedOrganization ? (
        <OrganizationManagementDashboard
          organization={selectedOrganization}
          back={() => {
            setSelectedOrganization(null);
            updateLists();
          }}
          updateOrganization={() => updateLists()}
        />
      ) : (
        <Container>
          <Drawer
            opened={openedOrgRequest}
            onClose={closeOrgRequest}
            withCloseButton={true}
            position="right"
            size="lg"
          >
            <OrganizationAccountRequestForm
              onSubmit={(body) => {
                createOrgRequest(
                  body,
                  distId,
                  (res) => {
                    console.log(res);
                    closeOrgRequest();
                    updateLists();
                  },
                  (error) => {
                    console.error(error);
                    showCreateOrgRequestError(error, t);
                  },
                );
              }}
            />
          </Drawer>
          <Drawer
            opened={openedOfflineRequest}
            onClose={closeOfflineRequest}
            withCloseButton={true}
            position="right"
            size="lg"
          >
            <OfflineLicenseRequestForm
              organizations={organizations}
              onSubmit={(body, setLoading) => {
                setLoading(true);
                createOfflineLicenseRequest(
                  body,
                  (res) => {
                    console.log(res);
                    setLoading(false);
                    closeOfflineRequest();
                    updateLists();
                  },
                  (error) => {
                    setLoading(false);
                    console.error(error);
                  },
                );
              }}
            />
          </Drawer>
          <Flex justify={"space-between"} align={"center"}>
            <Group>
              <Menu
                opened={menuOpened}
                onChange={setMenuOpened}
                shadow="md"
                width={200}
                position="bottom-start"
              >
                <Menu.Target>
                  <Burger
                    opened={menuOpened}
                    onClick={() => setMenuOpened((o) => !o)}
                    size="sm"
                    mr="md"
                  />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => navigate("/analytics-dashboard")}>
                    Analytics Dashboard
                  </Menu.Item>
                  <Menu.Item
                    component="a"
                    href={DISTRIBUTOR_DOCS_FOLDER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Docs
                  </Menu.Item>
                  <Menu.Item onClick={() => navigate("/orders")}>Orders</Menu.Item>
                  <Menu.Item onClick={() => navigate("/licenses")}>Licenses</Menu.Item>
                  <Menu.Item onClick={() => navigate("/invoices")}>Invoices</Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Title my={"sm"}>Manager Dashboard</Title>
            </Group>
            <Group>
              <Button onClick={openOrgRequest}>New Account Request</Button>
              <Button onClick={openOfflineRequest}>Offline License Request</Button>
            </Group>
          </Flex>
          <SubmissionsList />
          <OfflineLicenseRequestList changed={openedOfflineRequest} />
          {orgRequests.length > 0 && (
            <>
              <Card mt={"sm"} p={"sm"} style={{ background: "var(--oculera-surface-alt)" }}>
                <OrganizationRequestList
                  orgRequests={orgRequests}
                  deleteRequest={handleDeleteRequest}
                />
              </Card>
              <Divider size={"sm"} mt={"sm"} mb={"sm"} />
            </>
          )}
          <Group justify="space-between">
            <Text mt={"sm"}> Organizations </Text>
            <Switch
              onChange={() => setShowDetails(!showDetails)}
              onLabel={t("Show")}
              offLabel={t("Hide")}
            />
          </Group>
          <PaginatedOrganizations
            distId={distId}
            showDetails={showDetails}
            onOrganizationSelect={setSelectedOrganization}
            dateLocale="en-GB"
          />
        </Container>
      )}
    </Container>
  );
}
